// ==================== Base64 编码/解码 ====================

import CryptoJS from 'crypto-js';
import { JSEncrypt } from 'jsencrypt';

/**
 * Base64 编码
 */
export function base64Encode(str: string): string {
  if (!str) return '';
  const wordArray = CryptoJS.enc.Utf8.parse(str);
  return CryptoJS.enc.Base64.stringify(wordArray);
}

/**
 * Base64 解码
 */
export function base64Decode(str: string): string {
  if (!str) return '';
  try {
    const wordArray = CryptoJS.enc.Base64.parse(str);
    return CryptoJS.enc.Utf8.stringify(wordArray);
  } catch {
    return '';
  }
}

/**
 * Base64 URL 安全编码（替换 +/ 为 -_）
 */
export function base64UrlEncode(str: string): string {
  return base64Encode(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Base64 URL 安全解码
 */
export function base64UrlDecode(str: string): string {
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }
  return base64Decode(base64);
}

// Base64 命名空间
export const base64 = {
  encode: base64Encode,
  decode: base64Decode,
  urlEncode: base64UrlEncode,
  urlDecode: base64UrlDecode,
};

// ==================== 哈希函数 ====================

/**
 * 简单哈希函数 (djb2 算法)
 * 用于生成字符串的数字标识
 */
export function hash(str: string): number {
  let hashVal = 5381;
  for (let i = 0; i < str.length; i++) {
    hashVal = (hashVal * 33) ^ str.charCodeAt(i);
  }
  return hashVal >>> 0;
}

/**
 * 生成哈希字符串
 */
export function hashString(str: string): string {
  return hash(str).toString(36);
}

/**
 * MD5 哈希
 */
export function md5(str: string): string {
  if (!str) return '';
  return CryptoJS.MD5(str).toString();
}

/**
 * SHA1 哈希
 */
export function sha1(str: string): string {
  if (!str) return '';
  return CryptoJS.SHA1(str).toString();
}

/**
 * SHA256 哈希
 */
export function sha256(str: string): string {
  if (!str) return '';
  return CryptoJS.SHA256(str).toString();
}

/**
 * SHA512 哈希
 */
export function sha512(str: string): string {
  if (!str) return '';
  return CryptoJS.SHA512(str).toString();
}

/**
 * SHA3 哈希
 */
export function sha3(
  str: string,
  outputLength: 224 | 256 | 384 | 512 = 256,
): string {
  if (!str) return '';
  return CryptoJS.SHA3(str, { outputLength }).toString();
}

/**
 * RIPEMD160 哈希
 */
export function ripemd160(str: string): string {
  if (!str) return '';
  return CryptoJS.RIPEMD160(str).toString();
}

// ==================== HMAC 签名 ====================

/**
 * HMAC-MD5 签名
 */
export function hmacMd5(message: string, secret: string): string {
  if (!message) return '';
  return CryptoJS.HmacMD5(message, secret).toString();
}

/**
 * HMAC-SHA1 签名
 */
export function hmacSha1(message: string, secret: string): string {
  if (!message) return '';
  return CryptoJS.HmacSHA1(message, secret).toString();
}

/**
 * HMAC-SHA256 签名
 */
export function hmacSha256(message: string, secret: string): string {
  if (!message) return '';
  return CryptoJS.HmacSHA256(message, secret).toString();
}

/**
 * HMAC-SHA512 签名
 */
export function hmacSha512(message: string, secret: string): string {
  if (!message) return '';
  return CryptoJS.HmacSHA512(message, secret).toString();
}

/**
 * 验证 HMAC-SHA256 签名
 */
export function hmacVerify(
  message: string,
  signature: string,
  secret: string,
): boolean {
  const computed = hmacSha256(message, secret);
  return computed === signature;
}

// ==================== AES 加密/解密 ====================

// 默认 AES 配置
const DEFAULT_AES_KEY = 'antd-pro-admin-2024'; // 生产环境请更换为安全的密钥
const DEFAULT_AES_IV = '1234567890123456';

/**
 * AES 加密 (CBC 模式)
 * @param plaintext 明文
 * @param key 密钥，默认使用内置密钥
 * @param iv 初始向量，默认使用内置 IV
 */
export function aesEncrypt(
  plaintext: string,
  key?: string,
  iv?: string,
): string {
  if (!plaintext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key || DEFAULT_AES_KEY);
    const ivWordArray = CryptoJS.enc.Utf8.parse(iv || DEFAULT_AES_IV);

    const encrypted = CryptoJS.AES.encrypt(plaintext, keyWordArray, {
      iv: ivWordArray,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return encrypted.toString();
  } catch {
    console.error('aesEncrypt: 加密失败');
    return '';
  }
}

/**
 * AES 解密 (CBC 模式)
 */
export function aesDecrypt(
  ciphertext: string,
  key?: string,
  iv?: string,
): string {
  if (!ciphertext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key || DEFAULT_AES_KEY);
    const ivWordArray = CryptoJS.enc.Utf8.parse(iv || DEFAULT_AES_IV);

    const decrypted = CryptoJS.AES.decrypt(ciphertext, keyWordArray, {
      iv: ivWordArray,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('aesDecrypt: 解密失败');
    return '';
  }
}

/**
 * AES 加密 (ECB 模式，无需 IV)
 */
export function aesEncryptECB(plaintext: string, key?: string): string {
  if (!plaintext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key || DEFAULT_AES_KEY);

    const encrypted = CryptoJS.AES.encrypt(plaintext, keyWordArray, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });

    return encrypted.toString();
  } catch {
    console.error('aesEncryptECB: 加密失败');
    return '';
  }
}

/**
 * AES 解密 (ECB 模式)
 */
export function aesDecryptECB(ciphertext: string, key?: string): string {
  if (!ciphertext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key || DEFAULT_AES_KEY);

    const decrypted = CryptoJS.AES.decrypt(ciphertext, keyWordArray, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('aesDecryptECB: 解密失败');
    return '';
  }
}

/**
 * AES 加密（简化版，使用密码短语）
 * 内部自动处理 Key 和 IV
 */
export function encrypt(plaintext: string, passphrase: string): string {
  if (!plaintext) return '';
  try {
    return CryptoJS.AES.encrypt(plaintext, passphrase).toString();
  } catch {
    console.error('encrypt: 加密失败');
    return '';
  }
}

/**
 * AES 解密（简化版）
 */
export function decrypt(ciphertext: string, passphrase: string): string {
  if (!ciphertext) return '';
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, passphrase);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('decrypt: 解密失败');
    return '';
  }
}

// ==================== DES/3DES 加密解密 ====================

/**
 * DES 加密
 */
export function desEncrypt(plaintext: string, key: string): string {
  if (!plaintext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const encrypted = CryptoJS.DES.encrypt(plaintext, keyWordArray, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch {
    console.error('desEncrypt: 加密失败');
    return '';
  }
}

/**
 * DES 解密
 */
export function desDecrypt(ciphertext: string, key: string): string {
  if (!ciphertext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const decrypted = CryptoJS.DES.decrypt(ciphertext, keyWordArray, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('desDecrypt: 解密失败');
    return '';
  }
}

/**
 * 3DES 加密
 */
export function tripleDesEncrypt(plaintext: string, key: string): string {
  if (!plaintext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const encrypted = CryptoJS.TripleDES.encrypt(plaintext, keyWordArray, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch {
    console.error('tripleDesEncrypt: 加密失败');
    return '';
  }
}

/**
 * 3DES 解密
 */
export function tripleDesDecrypt(ciphertext: string, key: string): string {
  if (!ciphertext) return '';
  try {
    const keyWordArray = CryptoJS.enc.Utf8.parse(key);
    const decrypted = CryptoJS.TripleDES.decrypt(ciphertext, keyWordArray, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('tripleDesDecrypt: 解密失败');
    return '';
  }
}

// ==================== RC4 加密解密 ====================

/**
 * RC4 加密
 */
export function rc4Encrypt(plaintext: string, key: string): string {
  if (!plaintext) return '';
  try {
    return CryptoJS.RC4.encrypt(plaintext, key).toString();
  } catch {
    console.error('rc4Encrypt: 加密失败');
    return '';
  }
}

/**
 * RC4 解密
 */
export function rc4Decrypt(ciphertext: string, key: string): string {
  if (!ciphertext) return '';
  try {
    const bytes = CryptoJS.RC4.decrypt(ciphertext, key);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('rc4Decrypt: 解密失败');
    return '';
  }
}

// ==================== Rabbit 加密解密 ====================

/**
 * Rabbit 加密
 */
export function rabbitEncrypt(plaintext: string, key: string): string {
  if (!plaintext) return '';
  try {
    return CryptoJS.Rabbit.encrypt(plaintext, key).toString();
  } catch {
    console.error('rabbitEncrypt: 加密失败');
    return '';
  }
}

/**
 * Rabbit 解密
 */
export function rabbitDecrypt(ciphertext: string, key: string): string {
  if (!ciphertext) return '';
  try {
    const bytes = CryptoJS.Rabbit.decrypt(ciphertext, key);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error('rabbitDecrypt: 解密失败');
    return '';
  }
}

// ==================== RSA 加密解密 ====================

/**
 * RSA 加密 (使用公钥)
 * @param plaintext 明文
 * @param publicKey RSA公钥 (PEM格式或base64格式)
 */
export function rsaEncrypt(plaintext: string, publicKey: string): string {
  if (!plaintext || !publicKey) return '';
  try {
    // Normalize public key format
    const normalizedKey = normalizePublicKey(publicKey);

    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(normalizedKey);
    const encrypted = encrypt.encrypt(plaintext);
    return encrypted || '';
  } catch (error) {
    console.error('rsaEncrypt: RSA加密失败', error);
    return '';
  }
}

/**
 * 标准化公钥格式
 * @param publicKey 原始公钥
 */
function normalizePublicKey(publicKey: string): string {
  const normalizedKey = publicKey.trim();

  // 如果密钥没有PEM头尾，则包装它
  if (!normalizedKey.includes('-----BEGIN')) {
    // 移除所有空格和换行
    const cleanKey = normalizedKey.replace(/\s/g, '');

    // 将base64密钥包装为PEM格式，每64字符换行
    const wrappedKey = cleanKey.replace(/(.{64})/g, '$1\n');

    return `-----BEGIN PUBLIC KEY-----\n${wrappedKey}\n-----END PUBLIC KEY-----`;
  }

  return normalizedKey;
}

// ==================== Token 生成 ====================

/**
 * 生成随机 Token
 * @param length Token 长度，默认 32
 */
export function generateToken(length = 32): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  const randomWords = CryptoJS.lib.WordArray.random(length);
  const bytes = randomWords.toString(CryptoJS.enc.Hex);
  for (let i = 0; i < length; i++) {
    const index = parseInt(bytes.slice(i * 2, i * 2 + 2), 16) % chars.length;
    token += chars[index];
  }
  return token;
}

/**
 * 生成 UUID v4
 */
export function generateUUID(): string {
  const randomWords = CryptoJS.lib.WordArray.random(16);
  const bytes = randomWords.toString(CryptoJS.enc.Hex);

  return [
    bytes.slice(0, 8),
    bytes.slice(8, 12),
    `4${bytes.slice(13, 16)}`,
    ((parseInt(bytes.slice(16, 18), 16) & 0x3f) | 0x80).toString(16) +
      bytes.slice(18, 20),
    bytes.slice(20, 32),
  ].join('-');
}

/**
 * 生成随机字节（返回 Hex 字符串）
 */
export function randomBytes(length: number): string {
  return CryptoJS.lib.WordArray.random(length).toString(CryptoJS.enc.Hex);
}

/**
 * 生成随机十六进制字符串
 */
export function randomHex(length: number): string {
  const bytes = CryptoJS.lib.WordArray.random(Math.ceil(length / 2));
  return bytes.toString(CryptoJS.enc.Hex).slice(0, length);
}

// ==================== 编码转换 ====================

/**
 * 字符串转 Hex
 */
export function stringToHex(str: string): string {
  if (!str) return '';
  return CryptoJS.enc.Utf8.parse(str).toString(CryptoJS.enc.Hex);
}

/**
 * Hex 转字符串
 */
export function hexToString(hex: string): string {
  if (!hex) return '';
  try {
    return CryptoJS.enc.Hex.parse(hex).toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
}

/**
 * 字符串转 WordArray
 */
export function stringToWordArray(str: string): CryptoJS.lib.WordArray {
  return CryptoJS.enc.Utf8.parse(str);
}

/**
 * WordArray 转字符串
 */
export function wordArrayToString(wordArray: CryptoJS.lib.WordArray): string {
  return CryptoJS.enc.Utf8.stringify(wordArray);
}

// ==================== PBKDF2 密钥派生 ====================

/**
 * PBKDF2 密钥派生
 * @param password 密码
 * @param salt 盐值
 * @param keySize 密钥长度（字数，1字=4字节），默认 8（32字节）
 * @param iterations 迭代次数，默认 10000
 */
export function pbkdf2(
  password: string,
  salt: string,
  keySize = 8,
  iterations = 10000,
): string {
  const key = CryptoJS.PBKDF2(password, salt, {
    keySize,
    iterations,
  });
  return key.toString();
}

// 默认导出
export default {
  // Base64
  base64,
  base64Encode,
  base64Decode,
  base64UrlEncode,
  base64UrlDecode,
  // 哈希
  hash,
  hashString,
  md5,
  sha1,
  sha256,
  sha512,
  sha3,
  ripemd160,
  // HMAC
  hmacMd5,
  hmacSha1,
  hmacSha256,
  hmacSha512,
  hmacVerify,
  // AES
  aesEncrypt,
  aesDecrypt,
  aesEncryptECB,
  aesDecryptECB,
  encrypt,
  decrypt,
  // DES/3DES
  desEncrypt,
  desDecrypt,
  tripleDesEncrypt,
  tripleDesDecrypt,
  // RC4
  rc4Encrypt,
  rc4Decrypt,
  // Rabbit
  rabbitEncrypt,
  rabbitDecrypt,
  // Token
  generateToken,
  generateUUID,
  randomBytes,
  randomHex,
  // 编码
  stringToHex,
  hexToString,
  stringToWordArray,
  wordArrayToString,
  // PBKDF2
  pbkdf2,
  // RSA
  rsaEncrypt,
};
